import React, { lazy, Suspense, useEffect } from 'react'
import { DarkModeProvider } from './contexts/DarkModeContext'
import Header from './components/Header'
import Hero from './components/Hero'
import Features from './components/Features'
import Analytics from './utils/analytics'

// Lazy load components for better performance (keeping Features eager loaded)
const About = lazy(() => import('./components/About'))
const Products = lazy(() => import('./components/Products'))
const Values = lazy(() => import('./components/Values'))
const Contact = lazy(() => import('./components/Contact'))
const Footer = lazy(() => import('./components/Footer'))

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orifel"></div>
  </div>
)

// Initialize analytics
const analytics = Analytics()

function AppContent() {
  useEffect(() => {
    // Initialize analytics
    analytics.init()
    
    // Track page view
    analytics.trackPageView('Home', '/')
    
    // Track performance metrics
    analytics.trackPerformance()
    
    // Setup scroll depth tracking
    analytics.setupScrollTracking()
    
    // Add click tracking to important elements
    const setupClickTracking = () => {
      // Track CTA button clicks
      document.querySelectorAll('[data-analytics="cta"]').forEach(element => {
        element.addEventListener('click', () => {
          analytics.trackEvent('CTA Click', {
            element: element.textContent,
            location: element.closest('section')?.id || 'unknown'
          })
        })
      })

      // Track navigation clicks
      document.querySelectorAll('nav a').forEach(element => {
        element.addEventListener('click', () => {
          analytics.trackEvent('Navigation Click', {
            link: element.textContent,
            href: element.href
          })
        })
      })
    }

    // Setup click tracking after component mount
    setTimeout(setupClickTracking, 1000)

    // Track user engagement time
    const startTime = Date.now()
    const trackEngagement = () => {
      const engagementTime = Math.round((Date.now() - startTime) / 1000)
      analytics.trackEvent('Page Engagement', {
        timeOnPage: engagementTime,
        page: 'Home'
      })
    }

    // Track engagement on page unload
    window.addEventListener('beforeunload', trackEngagement)

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', trackEngagement)
    }
  }, [])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300" style={{ minHeight: '100vh', minHeight: '100dvh' }}>
      <Header />
      
      <main>
        <section id="hero">
          <Hero />
        </section>
        
        <Suspense fallback={<LoadingSpinner />}>
          <section id="about">
            <About />
          </section>
        </Suspense>
        
        <section id="features">
          <Features />
        </section>
        
        <Suspense fallback={<LoadingSpinner />}>
          <section id="products">
            <Products />
          </section>
        </Suspense>
        
        <Suspense fallback={<LoadingSpinner />}>
          <section id="values">
            <Values />
          </section>
        </Suspense>
        
        <Suspense fallback={<LoadingSpinner />}>
          <section id="contact">
            <Contact />
          </section>
        </Suspense>
      </main>
      
      <Suspense fallback={<LoadingSpinner />}>
        <Footer />
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <DarkModeProvider>
      <AppContent />
    </DarkModeProvider>
  )
}

export default App
